import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

type ToastVariant = "success" | "error" | "loading" | "info";

type ToastItem = {
  id: number;
  title: string;
  description?: string;
  variant: ToastVariant;
};

type ToastInput = Omit<ToastItem, "id">;

type ToastContextValue = {
  pushToast: (toast: ToastInput) => number;
  updateToast: (id: number, toast: Partial<ToastInput>) => void;
  dismissToast: (id: number) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_ACCENTS: Record<ToastVariant, string> = {
  success: "border-emerald-700/20 bg-emerald-50 text-emerald-900",
  error: "border-rose-700/20 bg-rose-50 text-rose-900",
  loading: "border-amber-700/20 bg-amber-50 text-amber-900",
  info: "border-primary/10 bg-surface text-primary",
};

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div className="pointer-events-none fixed right-4 bottom-4 z-[90] flex w-[min(100%,22rem)] flex-col gap-3 sm:right-6 sm:bottom-6">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`pointer-events-auto overflow-hidden rounded-[1.5rem] border px-4 py-3 shadow-[0_18px_45px_rgba(17,17,17,0.12)] backdrop-blur ${TOAST_ACCENTS[toast.variant]} animate-[onboarding-reveal_0.3s_ease-out]`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="text-sm font-semibold uppercase tracking-[0.16em]">{toast.title}</p>
              {toast.description ? (
                <p className="text-sm leading-6 opacity-80">{toast.description}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => onDismiss(toast.id)}
              className="text-xs uppercase tracking-[0.18em] opacity-60 transition-opacity hover:opacity-100"
            >
              Close
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const pushToast = (toast: ToastInput) => {
    const id = nextId.current++;
    setToasts((current) => [...current, { id, ...toast }]);
    return id;
  };

  const updateToast = (id: number, toast: Partial<ToastInput>) => {
    setToasts((current) =>
      current.map((item) => (item.id === id ? { ...item, ...toast } : item)),
    );
  };

  const dismissToast = (id: number) => {
    setToasts((current) => current.filter((item) => item.id !== id));
  };

  useEffect(() => {
    const timers = toasts
      .filter((toast) => toast.variant !== "loading")
      .map((toast) =>
        window.setTimeout(() => {
          dismissToast(toast.id);
        }, 4200),
      );

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [toasts]);

  const value = useMemo(
    () => ({ pushToast, updateToast, dismissToast }),
    [],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used inside ToastProvider.");
  }

  return context;
}
